import { createContext, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { wsUrl } from "@/lib/api";

const LiveContext = createContext({ connected: false });

export function LiveProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    let closed = false;

    const connect = () => {
      const token = localStorage.getItem("ecpl_token");
      if (!token) return;
      const ws = new WebSocket(wsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!closed) retryRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "jobs_changed") {
          qc.invalidateQueries({ queryKey: ["jobs"] });
          qc.invalidateQueries({ queryKey: ["stats"] });
          if (msg.job_id) qc.invalidateQueries({ queryKey: ["job", msg.job_id] });
        } else if (msg.type === "approvals_changed") {
          qc.invalidateQueries({ queryKey: ["approvals"] });
          qc.invalidateQueries({ queryKey: ["stats"] });
          if (msg.job_id) qc.invalidateQueries({ queryKey: ["job", msg.job_id] });
        } else if (msg.type === "users_changed") {
          qc.invalidateQueries({ queryKey: ["users"] });
          qc.invalidateQueries({ queryKey: ["captains"] });
          qc.invalidateQueries({ queryKey: ["clients"] });
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retryRef.current);
      wsRef.current?.close();
    };
  }, [qc]);

  const value = useMemo(() => ({ connected }), [connected]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);
